import { useState, useMemo, useCallback } from "react";

export type SortDirection = "asc" | "desc";

export interface UseTableSortOptions<TData, TColumn extends string = string> {
  data: TData[];
  defaultColumn?: TColumn;
  defaultDirection?: SortDirection;
  accessors?: Partial<Record<TColumn, (data: TData) => string | number>>;
}

export interface UseTableSortReturn<TData, TColumn extends string = string> {
  sortColumn: TColumn;
  sortDirection: SortDirection;
  handleSort: (column: TColumn) => void;
  sortedData: TData[];
}

export function useTableSort<TData, TColumn extends string = string>({
  data,
  defaultColumn,
  defaultDirection = "asc",
  accessors = {},
}: UseTableSortOptions<TData, TColumn>): UseTableSortReturn<TData, TColumn> {
  const [sortColumn, setSortColumn] = useState<TColumn | undefined>(
    defaultColumn
  );
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(defaultDirection);

  const handleSort = useCallback(
    (column: TColumn) => {
      if (sortColumn === column) {
        setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [sortColumn]
  );

  const sortedData = useMemo(() => {
    if (!sortColumn) return data;

    return [...data].sort((a, b) => {
      const accessor = accessors[sortColumn];

      let aVal: string | number = "";
      let bVal: string | number = "";

      if (accessor) {
        aVal = accessor(a);
        bVal = accessor(b);
      } else {
        aVal = (a as Record<string, string | number>)[sortColumn] ?? "";
        bVal = (b as Record<string, string | number>)[sortColumn] ?? "";
      }

      if (aVal === null || aVal === undefined) aVal = "";
      if (bVal === null || bVal === undefined) bVal = "";

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortColumn, sortDirection, accessors]);

  return {
    sortColumn: sortColumn as TColumn,
    sortDirection,
    handleSort,
    sortedData,
  };
}
